// A person's name and postal address. See the 20260921000700 migration for
// why this lives apart from userSettings.tsx: that one is preferences
// (timezone, notification channels), this one is identity.
//
// The Flutter mirror is app/mobile-app/lib/src/data/profile.dart.

import { supabase } from "./supabase";

export interface Profile {
  first_name: string | null;
  last_name: string | null;
  whatsapp_number: string | null;
  /** Which workspace whatsapp_number's automatic posting resolves to when the
   *  user belongs to more than one (20260923000800). Null means "resolve
   *  automatically if there's exactly one workspace, otherwise refuse." */
  whatsapp_workspace_id: string | null;
  /** Publish WhatsApp posts without the review step, unless the workspace
   *  requires an approver (20260924000100). */
  whatsapp_skip_review: boolean;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
}

export const EMPTY_PROFILE: Profile = {
  first_name: null, last_name: null, whatsapp_number: null, whatsapp_workspace_id: null, whatsapp_skip_review: false,
  address_line1: null, address_line2: null,
  city: null, state: null, postal_code: null, country: null,
};

export async function loadProfile(): Promise<Profile> {
  const { data: auth } = await supabase.auth.getUser();
  const id = auth.user?.id;
  if (!id) return EMPTY_PROFILE;

  const { data, error } = await supabase
    .from("profiles")
    .select("first_name, last_name, whatsapp_number, whatsapp_workspace_id, whatsapp_skip_review, address_line1, address_line2, city, state, postal_code, country")
    .eq("user_id", id)
    .maybeSingle();
  if (error) throw error;
  return data ?? EMPTY_PROFILE;
}

/** Blank inputs are absence, not an empty string the check constraints would reject. */
function blankToNull(s: string | null): string | null {
  const trimmed = s?.trim();
  return trimmed ? trimmed : null;
}

/** Syntax normalization only: ownership verification is a separate flow. */
function normalizeWhatsAppNumber(value: string | null): string | null {
  const digits = (value ?? "").replace(/[\s().-]/g, "");
  if (!digits) return null;
  if (!/^\+?[1-9]\d{7,14}$/.test(digits)) {
    throw new Error("Enter a WhatsApp number with country code, for example +14254398054.");
  }
  return `+${digits.replace(/^\+/, "")}`;
}

export async function saveProfile(next: Profile): Promise<Profile> {
  const { data: auth } = await supabase.auth.getUser();
  const id = auth.user?.id;
  if (!id) throw new Error("Not signed in.");

  const cleaned: Profile = {
    first_name: blankToNull(next.first_name),
    last_name: blankToNull(next.last_name),
    whatsapp_number: normalizeWhatsAppNumber(next.whatsapp_number),
    whatsapp_workspace_id: next.whatsapp_workspace_id || null,
    whatsapp_skip_review: next.whatsapp_skip_review,
    address_line1: blankToNull(next.address_line1),
    address_line2: blankToNull(next.address_line2),
    city: blankToNull(next.city),
    state: blankToNull(next.state),
    postal_code: blankToNull(next.postal_code),
    country: blankToNull(next.country),
  };

  const { error } = await supabase
    .from("profiles")
    .upsert({ user_id: id, ...cleaned }, { onConflict: "user_id" });
  if (error) {
    // profiles_whatsapp_number_unique (20260923000600): another account already
    // declared this number. Surfaced distinctly so it never reads like a
    // generic save failure.
    if (error.code === "23505" && error.message.includes("profiles_whatsapp_number_unique")) {
      throw new Error("This WhatsApp number is already declared on another account.");
    }
    throw error;
  }
  return cleaned;
}
